import React, {useState} from 'react'
import {ToggleWrapper, ToggleTab} from './Toggle.style'

/**
 * @typedef {'default'|'pill'|'underline'|'due'|'pillFilled'} ToggleVariant
 * @typedef {'sm'|'md'|'lg'} ToggleSize
 */

/**
 * Toggle
 *
 * Segmented button group — UI only, no routing.
 *
 * @param {object} props
 * @param {Array<{label: string, value: string}>} props.tabs
 * @param {ToggleVariant} [props.variant='default']
 * @param {ToggleSize} [props.size='md']
 * @param {number} [props.defaultIndex=0] - initially active tab (uncontrolled)
 * @param {number} [props.activeIndex] - controlled mode; omit for uncontrolled
 * @param {(tab: object, index: number) => void} [props.onChange]
 * @param {boolean} [props.showActiveBorder=true]
 * @param {(tab: object, isActive: boolean) => React.ReactNode} [props.renderTab]
 */
export function Toggle({
  tabs = [],
  variant = 'default',
  size = 'md',
  defaultIndex = 0,
  activeIndex: controlledIndex,
  onChange,
  showActiveBorder = true,
  renderTab,
  fullWidth = false,
  ...props
}) {
  const isControlled = controlledIndex !== undefined
  const [internalIndex, setInternalIndex] = useState(defaultIndex)
  const resolvedIndex = isControlled ? controlledIndex : internalIndex

  const handleClick = (tab, idx) => {
    if (!isControlled) setInternalIndex(idx)
    onChange?.(tab, idx)
  }

  return (
    <ToggleWrapper
      variant={variant}
      role="tablist"
      fullWidth={fullWidth}
      {...props}
    >
      {tabs.map((tab, idx) => (
        <ToggleTab
          key={tab.value}
          role="tab"
          variant={variant}
          size={size}
          isActive={idx === resolvedIndex}
          aria-selected={idx === resolvedIndex}
          onClick={() => handleClick(tab, idx)}
          showActiveBorder={showActiveBorder}
          fullWidth={fullWidth}
        >
          {renderTab ? renderTab(tab, idx === resolvedIndex) : tab.label}
        </ToggleTab>
      ))}
    </ToggleWrapper>
  )
}
